import { $isCodeNode } from "@lexical/code-core";
import { $findMatchingParent } from "@lexical/utils";
import {
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  type LexicalNode,
  type RangeSelection,
} from "lexical";
import type { InlineToken } from "../../core/tokens";
import { $createTokenNode, type TokenNode } from "./node";

/** Nothing goes between a token and what follows it when that is space or closing punctuation. */
const CLOSES = /^[\s.,;:!?)\]}'"»…]/;
/** Nothing goes between what precedes a token and the token when that is space or an opener. */
const OPENS = /[\s([{'"«—–-]$/;

const edgeText = (node: LexicalNode | null): string =>
  // A line break is the edge of a line, not a space to skip or to lean on.
  node === null || !$isTextNode(node) ? "" : node.getTextContent();

function $around(selection: RangeSelection): { before: string; after: string } {
  const { anchor } = selection;
  const node = anchor.getNode();
  if ($isTextNode(node)) {
    const text = node.getTextContent();
    return {
      before:
        anchor.offset > 0 ? text.slice(0, anchor.offset) : edgeText(node.getPreviousSibling()),
      after:
        anchor.offset < text.length ? text.slice(anchor.offset) : edgeText(node.getNextSibling()),
    };
  }
  if (!$isElementNode(node)) return { before: "", after: "" };
  return {
    before: edgeText(node.getChildAtIndex(anchor.offset - 1)),
    after: edgeText(node.getChildAtIndex(anchor.offset)),
  };
}

export type InsertTokenResult =
  | { ok: true; node: TokenNode }
  | { ok: false; reason: "invalid-target" };

/**
 * Puts a token at the selection and leaves the caret where typing should continue: after the
 * token and one space, never a doubled space, and never a space in front of punctuation.
 */
export function $insertToken(token: InlineToken): InsertTokenResult {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return { ok: false, reason: "invalid-target" };
  // Code is literal text; a reference inside it would be neither.
  if ($findMatchingParent(selection.anchor.getNode(), $isCodeNode) !== null) {
    return { ok: false, reason: "invalid-target" };
  }
  if (!selection.isCollapsed()) selection.removeText();

  const { before, after } = $around(selection);
  const node = $createTokenNode(token);
  // Formatting at the caret carries over, the way it does for typed text.
  node.setFormat(selection.format);
  const nodes: LexicalNode[] =
    before === "" || OPENS.test(before) ? [node] : [$createTextNode(" "), node];
  selection.insertNodes(nodes);

  const next = node.getNextSibling();
  if (after === "" || !CLOSES.test(after)) {
    const space = $createTextNode(" ").setFormat(selection.format);
    node.insertAfter(space);
    space.select(1, 1);
  } else if (/^\s/.test(after) && $isTextNode(next)) {
    next.select(1, 1);
  } else if ($isTextNode(next)) {
    next.select(0, 0);
  } else {
    node.selectEnd();
  }
  return { ok: true, node };
}
